import { Tag } from "primereact/tag";

import type { Timeline } from "@/modules/carga-trabalho";
import type { StatusAlocacao } from "@/modules/carga-de-trabalho";
import type { MarcasSimulacao } from "@/modules/recursos/simulacao";
import { Gantt } from "@/shared/components/gantt";
import { RotuloObra } from "@/shared/components/rotulo-obra";
import type { GanttColumn, GanttRow } from "@/shared/components/gantt";

import type { LocalObraRowModel } from "@/shared/components/gantt-carga/gantt-local-rows";
import { lineWidthRem, obraLineLayout, obraPillLayout } from "../../gantt-obras-layout";
import { ConflictBand } from "@/shared/components/conflict-band";
import { AllocationBar } from "@/shared/components/gantt-allocation-bar";
import { ObraLineMark } from "../gantt-obra-line";
import { LocalConflictBadge } from "../local-conflict-badge";
import { SIMULACAO_MARCA_CLASSES, SimulacaoMarcaTag } from "@/shared/components/simulacao-marca";
import type { GanttLocalObrasViewModel } from "./types-gantt-local-obras";
import baseStyles from "@/shared/components/gantt-carga/gantt.module.scss";
import obraStyles from "../../gantt-obras.module.scss";
import styles from "../gantt-local/gantt-local.module.scss";

const STATUS_LABEL: Record<StatusAlocacao, string> = {
  PLANEJADO: "Planejado",
  REALIZADO: "Realizado",
};

/** Rótulo "Planejado"/"Realizado" da alocação (valor vindo do core). */
function StatusTag({ status }: { status: StatusAlocacao | null }) {
  if (!status) return null;

  return (
    <Tag
      className="p-tag-xs"
      severity={status === "PLANEJADO" ? "info" : "success"}
      value={STATUS_LABEL[status]}
      data-testid="gantt-status-tag"
      data-status={status}
    />
  );
}

const COLUMNS: GanttColumn[] = [
  { key: "obra", header: "Obra", width: "var(--obra-col)", cellClassName: obraStyles.obraCell },
  {
    key: "local",
    header: "Local",
    width: "var(--local-col)",
    cellClassName: obraStyles.peopleCell,
  },
];

function toRow(
  row: LocalObraRowModel,
  timeline: Timeline,
  monthWidthRem: number,
  marcas: MarcasSimulacao,
  rotuloConflito: GanttLocalObrasViewModel["rotuloConflito"],
): GanttRow {
  const marca = marcas.get(row.key);

  return {
    key: row.key,
    testId: "gantt-row-obra",
    className: marca ? SIMULACAO_MARCA_CLASSES.linha : undefined,
    actionLabel: `Detalhes de ${row.obraRotulo.nome} - ${row.localNome}`,
    cells: [
      row.first ? (
        <div key="obra" className={obraStyles.obraTitle} title={row.obraRotulo.sigla}>
          <RotuloObra {...row.obraRotulo} className={obraStyles.obraName} />
        </div>
      ) : (
        // Mesma obra da linha anterior: mascara a divisória no topo, mesclando a célula Obra.
        <span key="obra-merge" className={styles.obraMerge} aria-hidden />
      ),
      <div key="local" className={obraStyles.person}>
        <span className={obraStyles.personName}>{row.localNome}</span>
        {row.hasAlocacao ? (
          <span className={styles.localMeta}>
            <StatusTag status={row.status} />
            {row.conflicts > 0 ? (
              <LocalConflictBadge label={rotuloConflito(row.conflicts)} count={row.conflicts} />
            ) : null}
          </span>
        ) : null}
        {marca ? <SimulacaoMarcaTag marca={marca} /> : null}
      </div>,
    ],
    content: (
      <>
        {row.bands.map((band) => (
          <ConflictBand
            key={band.key}
            groups={[band.group]}
            tratado={band.tone === "tratado"}
            left={band.left}
            width={band.width}
            className={
              band.tone === "tratado" ? baseStyles.conflictBandIgnored : baseStyles.conflictBand
            }
          />
        ))}
        {row.line ? (
          <ObraLineMark
            line={row.line}
            layout={obraLineLayout()}
            widthRem={lineWidthRem(row.line.width, timeline.months.length, monthWidthRem)}
          />
        ) : null}
        {row.pill ? (
          <AllocationBar
            item={row.pill}
            layout={obraPillLayout(row.line !== null)}
            marca={marca}
            marcaPreenchida
            vessel={false}
          />
        ) : null}
      </>
    ),
  };
}

/**
 * Gantt da perspectiva Local agrupada por Obra: colunas fixas de Obra e Local, uma linha por
 * alocação, com o status e, na área temporal, a linha da obra e a pílula com a duração.
 */
export function GanttLocalObrasView({
  rows,
  timeline,
  today,
  marcas,
  rotuloConflito,
  scale,
  monthWidthRem,
  unitMinWidth,
}: GanttLocalObrasViewModel) {
  return (
    <Gantt.Root
      className={styles.obraRoot}
      corpoRola
      scale={scale}
      today={today}
      columns={COLUMNS}
      rows={rows.map((row) => toRow(row, timeline, monthWidthRem, marcas, rotuloConflito))}
      withActions
      grid="none"
      unitMinWidth={unitMinWidth}
    />
  );
}
